import { useTranslator } from '@bluebeam/react-i18n';
import ButtonGroupOverlay from 'components/ButtonGroupOverlay';
import Pencil from 'components/Icons/Pencil';
import Trash from 'components/Icons/Trash';
import {
  StdList,
  StdListBody,
  StdListHeader,
  StdListHeaderCell,
  StdListHeaderCheckbox,
} from 'components/StdList';
import Immutable from 'immutable';
import * as React from 'react';
import { UpdateMultiplePubReq } from 'records/interactions/UpdateMultiplePubReq';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { Sheet } from 'records/Sheet';
import { PublishRequest, RawPublishRequest } from 'records/PublishRequest';
import { SelectOption } from 'components/inputs/BaseSelect';
import { useDisciplineSelect } from 'hooks/useDisciplineSelect';
import AuditListRow from './AuditListRow';

const gridTemplateColumns = '48px 96px 128px 1.7fr 1fr 140px 1fr';

interface Props extends WithAppStoreProps {
  publishRequestsBySheetNumber: Immutable.OrderedMap<
    PublishRequest['sheetnumber'],
    number
  >;
  deleteSelectedPages: () => void;
}

const AuditList: React.FC<Props> = ({
  appState,
  actions,
  publishRequestsBySheetNumber,
  deleteSelectedPages,
}) => {
  const { translate } = useTranslator(['auditList', 'disciplineFilterSection']);
  const { sheets, publishRequests, selectedPublishRequests } = appState;
  const { disciplineOptions } = useDisciplineSelect();

  const handleEditMulti = React.useCallback((): void => {
    const updateMultiPubReq = new UpdateMultiplePubReq();

    actions.createInteraction(updateMultiPubReq);
  }, []);

  const handleDeselectAll = React.useCallback((): void => {
    actions.clearSelectedPublishRequests();
    actions.clearInteractions();
  }, []);

  const handleSelectAll = React.useCallback(
    (checked: boolean): void => {
      const selectedPublishRequests = checked
        ? publishRequests.reduce(
            (acc, pr) => acc.add(pr.pageid),
            Immutable.Set<string>()
          )
        : Immutable.Set();

      actions.setSelectedPublishRequests(selectedPublishRequests);

      // Close multi edit overlay if open.
      if (selectedPublishRequests.size === 0) {
        actions.clearInteractions();
      }
    },
    [publishRequests]
  );

  const isDuplicateSheetNumber = React.useCallback(
    (publishRequest: PublishRequest) => {
      return (
        publishRequestsBySheetNumber.get(publishRequest.sheetnumber, 0) > 1
      );
    },
    [publishRequestsBySheetNumber]
  );

  const isSelected = React.useCallback(
    (publishRequest: PublishRequest) => {
      return selectedPublishRequests.has(publishRequest.pageid);
    },
    [selectedPublishRequests]
  );

  const handleSelect = React.useCallback(
    (publishRequest: PublishRequest) => (checked: boolean) => {
      const newSelectedPublishRequests = checked
        ? selectedPublishRequests.add(publishRequest.pageid)
        : selectedPublishRequests.delete(publishRequest.pageid);

      actions.setSelectedPublishRequests(newSelectedPublishRequests);

      if (newSelectedPublishRequests.size === 0) {
        actions.clearInteractions();
      }
    },
    [selectedPublishRequests]
  );

  const handleChange = React.useCallback(
    (publishRequest: PublishRequest) =>
      (key: keyof RawPublishRequest, val: number | string | null) => {
        actions.updatePublishRequest(publishRequest.set(key, val));
      },
    []
  );

  const versionNumber = React.useCallback(
    (publishRequest: PublishRequest) => {
      return sheets.has(publishRequest.sheetnumber)
        ? sheets.get(publishRequest.sheetnumber, new Sheet()).versions.size
        : 0;
    },
    [sheets]
  );

  const initialSheetDisciplineSelection = React.useCallback(
    (
      publishRequest: PublishRequest
    ): SelectOption<string | null> | undefined | null => {
      return disciplineOptions.has(publishRequest.discipline)
        ? disciplineOptions.get(publishRequest.discipline)
        : null;
    },
    [disciplineOptions]
  );

  return (
    <div>
      {selectedPublishRequests.size > 0 ? (
        <ButtonGroupOverlay
          components={[
            <div>
              {translate('auditList:itemsSelected', {
                count: selectedPublishRequests.size,
              })}
            </div>,
            <div
              className="row items-center cursor-pointer"
              onClick={handleEditMulti}
            >
              <Pencil width="13px" height="13px" />
              <div className="ml2">{translate('auditList:edit')}</div>
            </div>,
            <div
              className="row items-center cursor-pointer"
              onClick={deleteSelectedPages}
            >
              <Trash width="12px" />
              <div className="ml2">{translate('auditList:delete')}</div>
            </div>,
            <div className="cursor-pointer" onClick={handleDeselectAll}>
              {translate('auditList:deselect')}
            </div>,
          ]}
        />
      ) : null}
      <div style={{ height: 'calc(100vh - 153px)' }}>
        <StdList id="audit-list" gridTemplateColumns={gridTemplateColumns}>
          <StdListHeader>
            <StdListHeaderCheckbox
              id="audit-list-select-all-checkbox"
              checked={selectedPublishRequests.size === publishRequests.size}
              indeterminate={selectedPublishRequests.size > 0}
              onChange={handleSelectAll}
            />
            <StdListHeaderCell text={translate('auditList:image')} />
            <StdListHeaderCell text={translate('auditList:number')} />
            <StdListHeaderCell text={translate('auditList:title')} />
            <StdListHeaderCell text={translate('auditList:discipline')} />
            <StdListHeaderCell text={translate('auditList:issuedDate')} />
            <StdListHeaderCell text={translate('auditList:revisionComment')} />
          </StdListHeader>
          <StdListBody>
            {publishRequests.toList().map((publishRequest) => (
              <AuditListRow
                key={publishRequest.pageid}
                publishRequest={publishRequest}
                isSelected={isSelected(publishRequest)}
                isDuplicateSheetNumber={isDuplicateSheetNumber(publishRequest)}
                onSelect={handleSelect(publishRequest)}
                onChange={handleChange(publishRequest)}
                versionNumber={versionNumber(publishRequest)}
                sheetDisciplineOptions={Array.from(disciplineOptions.values())}
                initialSheetDisciplineSelection={initialSheetDisciplineSelection(
                  publishRequest
                )}
              />
            ))}
          </StdListBody>
        </StdList>
      </div>
    </div>
  );
};

export default withAppStore<Props>(AuditList);
